// moment-timezone, not plain moment: these helpers do .tz() conversions. The grid
// gets the timezone plugin via a side-effect import elsewhere in the app, but this
// module is imported directly by its tests, so it declares the dependency itself.
import moment from "moment-timezone";
import { GetTimeZoneFromAbbreviation } from "common/Constants";

// Resolving what the closer grid's Date / Time cells hand back into a real timestamp.
//
// Both cells are backed by a valueGetter that returns a DISPLAY string -- "MM/DD/YY"
// for Date, "HH:mm:ss" for Time -- while their edit components only call
// setEditCellValue with a full ISO timestamp once a complete value has been typed.
// So the value arriving at preProcessEditCellProps is a real timestamp on the happy
// path, but can still be the raw display string when an edit is committed without a
// complete entry (tabbing straight through a cell, a partial entry, a mask that
// hasn't filled).
//
// Writing that string into the row's `time` is silently destructive, because it only
// goes wrong later, on save. "07/27/26" parses server-side as midnight UTC, and
// UpdateEventsAsync converts UTC -> local with the trip's offset, so a CDT trip
// (offset -5) lands on 2026-07-26 19:00 -- the event jumps to the PREVIOUS DAY.
// "HH:mm:ss" is worse: it parses as today's date, moving the event by weeks.
//
// These helpers convert a display string back into the timestamp it actually
// represents, and return null for anything unusable so the caller can leave the
// row's existing `time` alone rather than corrupting it.

const ISO_LIKE = /^\d{4}-\d{2}-\d{2}T/;
const DATE_MMDDYY = /^(\d{2})\/(\d{2})\/(\d{2})$/;

// Seconds are optional. "14:52" is a complete, unambiguous time -- a closer who types
// it means 14:52:00 and there is nothing to guess -- so refusing it just discards good
// work. The cell's mask is "00:00:00" with visible placeholders, so tabbing out after
// typing four digits actually hands over "14:52:__"; the trailing [^\d]* accepts that
// and the bare "14:52" and "14:52:" forms alike.
//
// Half-typed seconds are still refused: "14:52:4_" could mean :04, :40 or :45, and a
// digit is present so [^\d]*$ cannot match it. Guessing there would reintroduce exactly
// the silent-wrong-value problem this module exists to prevent.
const TIME_HHMM_OPTIONAL_SS = /^(\d{2}):(\d{2})(?::(\d{2}))?[^\d]*$/;

/** Noon, so a date entered against a row with no time can't straddle a day boundary. */
const DEFAULT_HOUR = 12;

/**
 * Turn a Date-cell edit value into a UTC ISO timestamp.
 *
 * @param value        what the edit cell committed - ISO timestamp or "MM/DD/YY"
 * @param existingTime the row's current `time` (UTC ISO), preserved for time-of-day
 * @param timeZoneAbbr the trip's timezone abbreviation, e.g. "CDT"
 * @returns UTC ISO string, or null when the value can't be resolved
 */
export const resolveDateCellValue = (
  value: any,
  existingTime: any,
  timeZoneAbbr: string
): string | null => {
  if (typeof value !== "string" || value.length === 0) return null;
  if (ISO_LIKE.test(value)) return value;

  const parts = DATE_MMDDYY.exec(value);
  if (!parts) return null;

  const [, mm, dd, yy] = parts;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  const year = parseInt(yy, 10);
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

  const tz = GetTimeZoneFromAbbreviation(timeZoneAbbr);
  // Keep the row's existing time-of-day; only the calendar date is being set.
  const base = existingTime
    ? moment.utc(existingTime).tz(tz)
    : moment.tz(tz).hour(DEFAULT_HOUR).minute(0).second(0).millisecond(0);
  if (!base.isValid()) return null;

  const next = base.clone().year(2000 + year).month(month - 1).date(day);
  if (!next.isValid()) return null;
  // moment rolls overflow forward (02/31 -> Mar 3). Insist the result is the date
  // that was actually asked for, so an impossible entry is refused, not silently moved.
  if (next.month() !== month - 1 || next.date() !== day) return null;
  return next.utc().toISOString();
};

/**
 * Whether a discarded entry should be reported to the closer.
 *
 * Refusing an incomplete entry protects the data, but silently keeping the old
 * value looks identical to a successful edit — the closer walks away believing
 * they changed something. Warn when they genuinely typed something we couldn't
 * use; stay quiet when they simply moved through the cell.
 *
 * @param masked   the mask's current text, e.g. "07/26/__"
 * @param original the text the cell started with
 * @param resolved what resolveDate/TimeCellValue made of it (null = unusable)
 */
export const shouldWarnDiscardedEntry = (
  masked: any,
  original: any,
  resolved: string | null
): boolean => {
  if (resolved !== null) return false;              // committed fine
  if (typeof masked !== "string") return false;
  if (masked === original) return false;            // tabbed through untouched
  return /\d/.test(masked);                         // they actually typed something
};

/**
 * Turn a Time-cell edit value into a UTC ISO timestamp.
 *
 * @param value        what the edit cell committed - ISO timestamp or "HH:mm:ss"
 * @param existingTime the row's current `time` (UTC ISO), preserved for the date
 * @param timeZoneAbbr the trip's timezone abbreviation, e.g. "CDT"
 * @returns UTC ISO string, or null when the value can't be resolved
 */
export const resolveTimeCellValue = (
  value: any,
  existingTime: any,
  timeZoneAbbr: string
): string | null => {
  if (typeof value !== "string" || value.length === 0) return null;
  if (ISO_LIKE.test(value)) return value;

  const parts = TIME_HHMM_OPTIONAL_SS.exec(value);
  if (!parts) return null;

  const [, hh, mi, ss] = parts;
  const hours = parseInt(hh, 10);
  const minutes = parseInt(mi, 10);
  // Omitted seconds mean zero, not "keep whatever was there" -- typing 14:52 over an
  // event at 14:52:47 should land on 14:52:00, because that is what was typed.
  const seconds = ss === undefined ? 0 : parseInt(ss, 10);
  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
  if (hours > 23 || minutes > 59 || seconds > 59) return null;

  const tz = GetTimeZoneFromAbbreviation(timeZoneAbbr);
  // Without an existing date there's nothing to anchor a bare time to. Refuse
  // rather than silently stamping today onto the row.
  if (!existingTime) return null;

  const base = moment.utc(existingTime).tz(tz);
  if (!base.isValid()) return null;

  const next = base.clone().hours(hours).minutes(minutes).seconds(seconds).milliseconds(0);
  return next.isValid() ? next.utc().toISOString() : null;
};
